/**
 * Event Service
 *
 * Provides functionality for working with event data.
 */

import { apiClient } from '../../core/api/client';
import { ApiService } from '../../core/api/service';
import { ApiResponse } from "../../../model";

import {
    EventCreateEventMembersRequest,
    EventCreateEventMembersResponse,
    EventCreateEventsRequest,
    EventCreateEventsResponseItem,
    EventDeleteEventMembersRequest,
    EventDeleteEventMembersResponse,
    EventDeleteEventsRequest,
    EventDeleteEventsResponseItem,
    EventGetCandidateMembersRequest,
    EventGetCandidateMembersResponseItem,
    EventGetEventMembersRequest,
    EventGetEventMembersResponse,
    EventGetEventRequest,
    EventGetEventResponseItem,
    EventSearchRequest,
    EventSearchResponseItem,
    EventUpdateEventMembersRequest,
    EventUpdateEventMembersResponse,
    EventUpdateEventsRequest,
    EventUpdateEventsResponseItem
} from "../../../model/event/dto/event-api";

export class EventService extends ApiService {
    /**
     * Search events based on criteria
     */
    async searchEvents(request: EventSearchRequest): Promise<EventSearchResponseItem[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventSearchResponseItem[]>>(
                '/api/event/search',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get event details by ID or name
     */
    async getEvent(request: EventGetEventRequest): Promise<EventGetEventResponseItem | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventGetEventResponseItem>>(
                '/api/event/getEvent',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get candidate members for events
     */
    async getCandidateMembers(request: EventGetCandidateMembersRequest): Promise<EventGetCandidateMembersResponseItem[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventGetCandidateMembersResponseItem[]>>(
                '/api/event/getCandidateMembers',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get members for a specific event
     */
    async getEventMembers(request: EventGetEventMembersRequest): Promise<EventGetEventMembersResponse[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventGetEventMembersResponse[]>>(
                '/api/event/getEventMembers',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Create new events
     */
    async createEvent(request: EventCreateEventsRequest): Promise<EventCreateEventsResponseItem | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventCreateEventsResponseItem>>(
                '/api/event/create',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update existing events
     */
    async updateEvent(request: EventUpdateEventsRequest): Promise<EventUpdateEventsResponseItem | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventUpdateEventsResponseItem>>(
                '/api/event/update',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Delete events
     */
    async deleteEvent(request: EventDeleteEventsRequest): Promise<EventDeleteEventsResponseItem | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventDeleteEventsResponseItem>>(
                '/api/event/delete',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Create new event members
     */
    async createEventMembers(request: EventCreateEventMembersRequest): Promise<EventCreateEventMembersResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventCreateEventMembersResponse>>(
                '/api/event/createEventMembers',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update existing event members
     */
    async updateEventMembers(request: EventUpdateEventMembersRequest): Promise<EventUpdateEventMembersResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventUpdateEventMembersResponse>>(
                '/api/event/updateEventMembers',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Delete event members
     */
    async deleteEventMembers(request: EventDeleteEventMembersRequest): Promise<EventDeleteEventMembersResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<EventDeleteEventMembersResponse>>(
                '/api/event/deleteEventMembers',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }
}

// Export a singleton instance
export const eventService = new EventService();
